<template>
  <div class="curator-review-container">
    <div class="curator-review-panel">
      <h2>Gene Symbol Curator Review</h2>

      <!-- Tabs -->
      <div class="tabs">
        <button
          :class="['tab-btn', { active: activeTab === 'pending' }]"
          @click="switchTab('pending')"
        >
          Pending Review
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'reviewed' }]"
          @click="switchTab('reviewed')"
        >
          Reviewed Symbols
        </button>
      </div>

      <!-- Pending Tab -->
      <div v-if="activeTab === 'pending'">
        <p class="subtitle">
          Review and approve pending gene class symbol registrations.
        </p>

        <div v-if="loading" class="loading-message">
          Loading pending symbols...
        </div>

        <div v-else-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <div v-else-if="pendingSymbols.length === 0" class="empty-message">
          No pending gene symbols to review.
        </div>

        <table v-else class="symbols-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Full Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Submitter</th>
              <th>Submitted</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="symbol in pendingSymbols" :key="symbol.TAIR_OBJECT_ID">
              <td class="symbol-name">{{ symbol.SYMBOL }}</td>
              <td>{{ symbol.DESCRIPTIVE_SYMBOL || "—" }}</td>
              <td>
                {{
                  symbol.HAS_MUTANT_PHENOTYPE === "T"
                    ? "Mutant Phenotype"
                    : "Gene Product"
                }}
              </td>
              <td>
                <span
                  :class="[
                    'status-badge',
                    symbol.STATUS === 'await_approval'
                      ? 'status-new'
                      : 'status-pending',
                  ]"
                >
                  {{ symbol.STATUS === "await_approval" ? "New" : "Pending" }}
                </span>
              </td>
              <td>{{ symbol.SUBMITTER_INFO || "—" }}</td>
              <td>{{ formatDate(symbol.RESERVATION_DATE) }}</td>
              <td>
                <router-link
                  :to="{
                    path: '/curator/gene_symbol_review',
                    query: { id: symbol.TAIR_OBJECT_ID },
                  }"
                  class="review-btn"
                >
                  Review
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Reviewed Tab -->
      <div v-if="activeTab === 'reviewed'">
        <p class="subtitle">
          Search and edit reviewed gene class symbols.
        </p>

        <!-- Search / Filter -->
        <div class="filter-bar">
          <input
            type="text"
            v-model="searchQuery"
            @input="debouncedFetchReviewed"
            placeholder="Search by symbol or full name..."
            class="search-input"
          />
          <select v-model="statusFilter" @change="fetchReviewedSymbols" class="status-select">
            <option value="">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="obsolete">Obsolete</option>
          </select>
        </div>

        <div v-if="reviewedLoading" class="loading-message">
          Loading reviewed symbols...
        </div>

        <div v-else-if="reviewedError" class="error-message">
          {{ reviewedError }}
        </div>

        <div v-else-if="reviewedSymbols.length === 0" class="empty-message">
          No reviewed gene symbols found.
        </div>

        <table v-else class="symbols-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Full Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Submitter</th>
              <th>Submitted</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="symbol in reviewedSymbols" :key="symbol.TAIR_OBJECT_ID">
              <td class="symbol-name">{{ symbol.SYMBOL }}</td>
              <td>{{ symbol.DESCRIPTIVE_SYMBOL || "—" }}</td>
              <td>
                {{
                  symbol.HAS_MUTANT_PHENOTYPE === "T"
                    ? "Mutant Phenotype"
                    : "Gene Product"
                }}
              </td>
              <td>
                <span
                  :class="[
                    'status-badge',
                    'status-' + symbol.STATUS,
                  ]"
                >
                  {{ symbol.STATUS }}
                </span>
              </td>
              <td>{{ symbol.SUBMITTER_INFO || "—" }}</td>
              <td>{{ formatDate(symbol.RESERVATION_DATE) }}</td>
              <td>
                <router-link
                  :to="{
                    path: '/curator/gene_symbol_review',
                    query: { id: symbol.TAIR_OBJECT_ID, mode: 'edit' },
                  }"
                  class="edit-btn"
                >
                  Edit
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="reviewedSymbols.length > 0 && hasMoreReviewed" class="load-more">
          <button class="load-more-btn" :disabled="reviewedLoading" @click="loadMoreReviewed">
            {{ reviewedLoading ? "Loading..." : "Load More" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/services/Api";
import { mapGetters } from "vuex";

export default {
  name: "GeneSymbolReviewList",
  data() {
    return {
      activeTab: "pending",
      pendingSymbols: [],
      loading: true,
      errorMessage: "",
      reviewedSymbols: [],
      reviewedLoading: false,
      reviewedError: "",
      reviewedOffset: 0,
      hasMoreReviewed: true,
      searchQuery: "",
      statusFilter: "",
      searchTimeout: null,
    };
  },
  computed: {
    ...mapGetters({
      getCommunityId: "authProfile/getCommunityId",
      getIsTairCurator: "authProfile/getIsTairCurator",
    }),
  },
  async created() {
    const tab = this.$route.query.tab;
    if (tab === "reviewed") {
      this.activeTab = "reviewed";
    }
    await this.fetchPendingSymbols();
    if (this.activeTab === "reviewed") {
      await this.fetchReviewedSymbols();
    }
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === "reviewed" && this.reviewedSymbols.length === 0 && !this.reviewedLoading) {
        this.fetchReviewedSymbols();
      }
    },
    async fetchPendingSymbols() {
      this.loading = true;
      try {
        const communityId = this.getCommunityId;
        const response = await Api().get("gene_class_symbol/pending", {
          params: { communityId },
        });
        if (response.data && response.data.rows) {
          this.pendingSymbols = response.data.rows;
        }
      } catch (error) {
        console.error("Error fetching pending symbols:", error);
        this.errorMessage =
          error.response && error.response.status === 403
            ? "Access denied. You must be a TAIR curator to review gene symbols."
            : "Error loading pending gene symbols. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async fetchReviewedSymbols(append = false) {
      this.reviewedLoading = true;
      this.reviewedError = "";
      if (!append) {
        this.reviewedOffset = 0;
        this.hasMoreReviewed = true;
      }
      try {
        const communityId = this.getCommunityId;
        const params = { communityId, offset: this.reviewedOffset };
        if (this.searchQuery.trim()) {
          params.search = this.searchQuery.trim();
        }
        if (this.statusFilter) {
          params.status = this.statusFilter;
        }
        const response = await Api().get("gene_class_symbol/reviewed", {
          params,
        });
        if (response.data && response.data.rows) {
          if (append) {
            this.reviewedSymbols = this.reviewedSymbols.concat(response.data.rows);
          } else {
            this.reviewedSymbols = response.data.rows;
          }
          // If fewer than pageSize returned, no more pages
          if (response.data.rows.length < (response.data.pageSize || 50)) {
            this.hasMoreReviewed = false;
          }
        }
      } catch (error) {
        console.error("Error fetching reviewed symbols:", error);
        this.reviewedError =
          error.response && error.response.status === 403
            ? "Access denied. Curator access required."
            : "Error loading reviewed gene symbols.";
      } finally {
        this.reviewedLoading = false;
      }
    },
    loadMoreReviewed() {
      this.reviewedOffset += 50;
      this.fetchReviewedSymbols(true);
    },
    debouncedFetchReviewed() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchReviewedSymbols();
      }, 300);
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.curator-review-container {
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-sizing: border-box;
}

.curator-review-panel {
  border: 1px solid #ccc;
  padding: 20px;
  margin: 0 auto;
  width: 100%;
  max-width: 1100px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: #fff;
}

.tabs {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border-bottom: 2px solid #ddd;
}

.tab-btn {
  padding: 10px 24px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  font-size: 1em;
  font-weight: bold;
  color: #666;
  transition: color 0.2s, border-color 0.2s;
}

.tab-btn:hover {
  color: #333;
}

.tab-btn.active {
  color: #007bff;
  border-bottom-color: #007bff;
}

.subtitle {
  color: #666;
  margin-bottom: 20px;
}

.filter-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.search-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
}

.status-select {
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
  min-width: 150px;
}

.symbols-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.symbols-table th,
.symbols-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.symbols-table th {
  background-color: #f5f5f5;
  font-weight: bold;
  border-bottom: 2px solid #ddd;
}

.symbols-table tbody tr:hover {
  background-color: #f9f9f9;
}

.symbol-name {
  font-weight: bold;
  font-family: monospace;
  font-size: 1.05em;
}

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.85em;
  font-weight: bold;
  text-transform: capitalize;
}

.status-new {
  background-color: #fff3cd;
  color: #856404;
}

.status-pending {
  background-color: #cce5ff;
  color: #004085;
}

.status-approved {
  background-color: #d4edda;
  color: #155724;
}

.status-obsolete {
  background-color: #f5c6cb;
  color: #721c24;
}

.review-btn,
.edit-btn {
  display: inline-block;
  padding: 5px 14px;
  color: white;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.9em;
  transition: background-color 0.2s;
}

.review-btn {
  background-color: #007bff;
}

.review-btn:hover {
  background-color: #0056b3;
}

.edit-btn {
  background-color: #28a745;
}

.edit-btn:hover {
  background-color: #218838;
}

.loading-message,
.empty-message {
  padding: 30px;
  text-align: center;
  color: #666;
  font-size: 1.1em;
}

.error-message {
  color: #dc3545;
  background-color: #f8d7da;
  padding: 12px;
  border-radius: 5px;
}

.load-more {
  text-align: center;
  padding: 20px;
}

.load-more-btn {
  padding: 8px 24px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95em;
}

.load-more-btn:hover:not(:disabled) {
  background-color: #0056b3;
}

.load-more-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
